import { useEffect, useMemo, useRef, useState } from "react";
import { GameButton } from "../components/GameButton";
import { VerifiedCreatorMark } from "../components/VerifiedCreatorMark";
import { NumgnumCard } from "../components/NumgnumCard";
import { NUMGNUM_NAMES, type Edition } from "../lib/cards";
import { hasSupabaseConfiguration, supabase } from "../lib/supabase";

type MarketCard = {
  level: number;
  edition: Edition;
  is_shiny: boolean;
  serial_number: number;
  is_founder: boolean;
  founder_username: string;
};

type MarketListing = {
  id: string;
  seller_username: string;
  is_mine?: boolean;
  status?: "active" | "sold" | "cancelled";
  play_price: number;
  win_price: number;
  created_at: string;
  cards: MarketCard[];
};

type MarketProfile = {
  id: string;
  username: string;
  play_points: number;
  win_points: number;
};

type SellCard = MarketCard & {
  id: string;
  state: string;
};

type MarketSection = "browse" | "sell" | "mine";

type ListingCursor = Pick<MarketListing, "created_at" | "id">;

type MarketCardGroup = MarketCard & {
  count: number;
  key: string;
  maximumSerial: number;
  minimumSerial: number;
};

const LISTINGS_PER_PAGE = 24;

function priceLabel(listing: Pick<MarketListing, "play_price" | "win_price">) {
  return [
    listing.play_price > 0 ? `${listing.play_price} Play Point${listing.play_price === 1 ? "" : "s"}` : null,
    listing.win_price > 0 ? `${listing.win_price} Win Point${listing.win_price === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" + ");
}

function playerFacingError(error: unknown) {
  const message = error instanceof Error
    ? error.message
    : typeof error === "object" && error !== null && "message" in error && typeof error.message === "string"
      ? error.message
      : "The Exchange lost the island signal.";
  return message.replace(/Grand Exchange/gi, "Exchange").replace(/battle hoard/gi, "Battle Hoard");
}

function groupMarketCards(cards: MarketCard[]) {
  const groups = new Map<string, MarketCardGroup>();

  for (const card of cards) {
    const key = [
      card.level,
      card.edition,
      card.is_shiny ? "shiny" : "standard",
      card.is_founder ? "founder" : "regular",
      card.is_founder ? card.founder_username : "",
    ].join(":");
    const existing = groups.get(key);
    if (existing) {
      existing.count += 1;
      existing.minimumSerial = Math.min(existing.minimumSerial, card.serial_number);
      existing.maximumSerial = Math.max(existing.maximumSerial, card.serial_number);
    } else {
      groups.set(key, {
        ...card,
        count: 1,
        key,
        minimumSerial: card.serial_number,
        maximumSerial: card.serial_number,
      });
    }
  }

  return [...groups.values()];
}

function serialRangeLabel(group: MarketCardGroup) {
  return group.minimumSerial === group.maximumSerial
    ? `NO. ${group.minimumSerial}`
    : `NOS. ${group.minimumSerial}\u2013${group.maximumSerial}`;
}

function GroupedListingCards({ cards }: { cards: MarketCard[] }) {
  return (
    <div className="market-listing-cards">
      {groupMarketCards(cards).map((group) => (
        <div className="market-card-group" key={group.key}>
          {group.count > 1 && <span className="market-card-group__count" aria-label={`${group.count} matching cards`}>{group.count}</span>}
          <NumgnumCard
            level={group.level}
            edition={group.edition}
            shiny={group.is_shiny}
            founder={group.is_founder}
            serial={group.minimumSerial}
            founderName={group.founder_username}
          />
          <span className="market-card-group__serials">{serialRangeLabel(group)}</span>
        </div>
      ))}
    </div>
  );
}

function listingClassName(cards: MarketCard[]) {
  return groupMarketCards(cards).length > 1
    ? "market-listing market-listing--multi"
    : "market-listing";
}

export function MarketPage() {
  const [section, setSection] = useState<MarketSection>("browse");
  const [listings, setListings] = useState<MarketListing[]>([]);
  const [myListings, setMyListings] = useState<MarketListing[]>([]);
  const [profile, setProfile] = useState<MarketProfile | null>(null);
  const [sellCards, setSellCards] = useState<SellCard[]>([]);
  const [battleHoardIDs, setBattleHoardIDs] = useState<Set<string>>(new Set());
  const [houseDisplayCardIDs, setHouseDisplayCardIDs] = useState<Set<string>>(new Set());
  const [selectedCardIDs, setSelectedCardIDs] = useState<Set<string>>(new Set());
  const [playPrice, setPlayPrice] = useState(0);
  const [winPrice, setWinPrice] = useState(0);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(Boolean(supabase));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreListings, setHasMoreListings] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [pendingPurchase, setPendingPurchase] = useState<MarketListing | null>(null);
  const listingCursorRef = useRef<ListingCursor | null>(null);
  const isLoadingMoreRef = useRef(false);
  const browseScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    let active = true;

    async function openMarket(userID?: string) {
      setIsLoading(true);
      try {
        await loadPublicListings();
        if (userID) await loadPlayerMarket(userID);
        else clearPlayerMarket();
      } catch (error) {
        if (active) setMessage(playerFacingError(error));
      } finally {
        if (active) setIsLoading(false);
      }
    }

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      window.setTimeout(() => {
        if (active) void openMarket(session?.user.id);
      }, 0);
    });

    void supabase.auth.getSession().then(({ data, error }) => {
      if (!active) return;
      if (error) {
        setMessage(error.message);
        setIsLoading(false);
      } else {
        void openMarket(data.session?.user.id);
      }
    });

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const root = browseScrollRef.current;
    const target = loadMoreRef.current;
    if (!root || !target || section !== "browse" || !hasMoreListings || isLoading || query.trim()) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) void loadPublicListings(true);
    }, { root, rootMargin: "420px 0px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [section, hasMoreListings, isLoading, listings.length, query]);

  async function loadPublicListings(append = false) {
    if (!supabase || (append && (!hasMoreListings || isLoadingMoreRef.current))) return;
    const cursor = append ? listingCursorRef.current : null;
    if (append) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
    }

    try {
      const { data, error } = await supabase.rpc("web_marketplace_feed_page", {
        p_limit: LISTINGS_PER_PAGE,
        p_after_created_at: cursor?.created_at ?? null,
        p_after_id: cursor?.id ?? null,
      });
      if (error) throw error;

      const page = (data ?? []) as MarketListing[];
      const lastListing = page.at(-1);
      listingCursorRef.current = lastListing
        ? { created_at: lastListing.created_at, id: lastListing.id }
        : cursor;
      setHasMoreListings(page.length === LISTINGS_PER_PAGE);
      setListings((current) => {
        if (!append) return page;
        const merged = new Map(current.map((listing) => [listing.id, listing]));
        page.forEach((listing) => merged.set(listing.id, listing));
        return [...merged.values()];
      });
    } catch (error) {
      if (!append) throw error;
      setMessage(`${playerFacingError(error)} The listings already on the floor are still available.`);
    } finally {
      if (append) {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }

  async function loadPlayerMarket(userID: string) {
    if (!supabase) return;
    const claimResult = await supabase.rpc("claim_configured_admin_account");
    if (claimResult.error) throw claimResult.error;
    const claimedProfile = claimResult.data as { id?: string } | null;
    const playerID = claimedProfile?.id ?? userID;

    const [profileResult, cardResult, discoveryResult, hoardResult, houseDisplayResult, myListingsResult] = await Promise.all([
      supabase.from("profiles").select("id,username,play_points,win_points").eq("id", playerID).single(),
      supabase.from("card_instances").select("id,level,edition,is_shiny,serial_number,state").eq("owner_id", playerID).neq("state", "forged").order("level").order("edition").order("serial_number"),
      supabase.from("numnum_discoveries").select("card_id,founder_username"),
      supabase.from("battle_decks").select("battle_deck_cards(card_id)").eq("owner_id", playerID).maybeSingle(),
      supabase.from("house_card_displays").select("card_id").eq("player_id", playerID),
      supabase.rpc("marketplace_my_listings", { p_limit: 300 }),
    ]);
    const error = profileResult.error ?? cardResult.error ?? discoveryResult.error ?? hoardResult.error ?? houseDisplayResult.error ?? myListingsResult.error;
    if (error || !profileResult.data) throw error ?? new Error("Your Exchange account could not be opened.");

    const founders = new Map((discoveryResult.data ?? []).map((row) => [row.card_id, row.founder_username]));
    setProfile(profileResult.data as MarketProfile);
    setSellCards((cardResult.data ?? []).map((card) => ({
      ...card,
      is_founder: founders.has(card.id),
      founder_username: founders.get(card.id) ?? "THE FORGE",
    })) as SellCard[]);
    const hoardCards = (hoardResult.data?.battle_deck_cards ?? []) as { card_id: string }[];
    setBattleHoardIDs(new Set(hoardCards.map((row) => row.card_id)));
    setHouseDisplayCardIDs(new Set((houseDisplayResult.data ?? []).map((row) => row.card_id)));
    setMyListings(((myListingsResult.data ?? []) as MarketListing[])
      .filter((listing) => listing.status === "active")
      .map((listing) => ({
        ...listing,
        is_mine: true,
        cards: listing.cards.map((card) => ({
          ...card,
          is_founder: card.is_founder ?? false,
          founder_username: card.founder_username ?? "THE FORGE",
        })),
      })));
  }

  function clearPlayerMarket() {
    setProfile(null);
    setSellCards([]);
    setMyListings([]);
    setBattleHoardIDs(new Set());
    setHouseDisplayCardIDs(new Set());
    setSelectedCardIDs(new Set());
    setSection("browse");
  }

  async function refreshEverything() {
    if (!supabase) return;
    const { data } = await supabase.auth.getSession();
    await loadPublicListings();
    if (data.session) await loadPlayerMarket(data.session.user.id);
  }

  const visibleListings = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return listings;
    return listings.filter((listing) => listing.seller_username.toLowerCase().includes(search)
      || listing.cards.some((card) => (NUMGNUM_NAMES[card.level] ?? `NUMGNUM ${card.level}`).toLowerCase().includes(search)
        || card.edition.toLowerCase().includes(search)
        || String(card.serial_number).includes(search)));
  }, [listings, query]);

  const availableSellCards = sellCards.filter((card) => card.state === "available"
    && card.serial_number > 0
    && !battleHoardIDs.has(card.id)
    && !houseDisplayCardIDs.has(card.id));
  const activeMyListings = myListings;

  function toggleCard(id: string) {
    setSelectedCardIDs((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else if (next.size < 20) next.add(id);
      return next;
    });
  }

  async function createListing() {
    if (!supabase || !profile || isWorking) return;
    if (!selectedCardIDs.size) {
      setMessage("Choose at least one card to list.");
      return;
    }
    if (playPrice < 0 || winPrice < 0 || (playPrice === 0 && winPrice === 0)) {
      setMessage("Set a Play Point price, Win Point price, or both.");
      return;
    }

    setIsWorking(true);
    setMessage("Opening your Exchange counter…");
    try {
      const { error } = await supabase.rpc("create_marketplace_listing", {
        p_card_ids: [...selectedCardIDs],
        p_play_price: playPrice,
        p_win_price: winPrice,
      });
      if (error) throw error;
      setSelectedCardIDs(new Set());
      setPlayPrice(0);
      setWinPrice(0);
      await refreshEverything();
      setSection("mine");
      setMessage("Your cards are listed and held safely by the Exchange until they sell or you withdraw them.");
    } catch (error) {
      setMessage(playerFacingError(error));
    } finally {
      setIsWorking(false);
    }
  }

  async function purchaseListing(listing: MarketListing) {
    if (!supabase || !profile || isWorking) return;
    setIsWorking(true);
    setMessage(`Trading with ${listing.seller_username}…`);
    try {
      const { error } = await supabase.rpc("purchase_marketplace_listing", {
        p_listing_id: listing.id,
        p_request_id: crypto.randomUUID(),
      });
      if (error) throw error;
      setPendingPurchase(null);
      await refreshEverything();
      setMessage(`${listing.cards.length} card${listing.cards.length === 1 ? " is" : "s are"} now in your Hoard. ${priceLabel(listing)} moved to ${listing.seller_username}.`);
    } catch (error) {
      setPendingPurchase(null);
      setMessage(playerFacingError(error));
    } finally {
      setIsWorking(false);
    }
  }

  async function withdrawListing(listing: MarketListing) {
    if (!supabase || !profile || isWorking) return;
    setIsWorking(true);
    setMessage("Withdrawing your listing…");
    try {
      const { error } = await supabase.rpc("cancel_marketplace_listing", { p_listing_id: listing.id });
      if (error) throw error;
      await refreshEverything();
      setMessage("Listing withdrawn. Its cards are available in your Hoard again.");
    } catch (error) {
      setMessage(playerFacingError(error));
    } finally {
      setIsWorking(false);
    }
  }

  const canAfford = (listing: MarketListing) => Boolean(profile)
    && profile!.play_points >= listing.play_price
    && profile!.win_points >= listing.win_price;

  return (
    <div className="market-page market-page--live page-scene">
      <header className="market-live-heading">
        <div>
          <p className="eyebrow">The Exchange · Beach Economics</p>
          <h1>Compound the Hoard.</h1>
          <p>Buy low. Sell weird. Let Play Points and Win Points compound until you reach Coast FI, get bougie, and stack enough aura to leave the grind on read.</p>
          <div className="market-wealth-track" aria-label="The Exchange path to imaginary wealth">
            <span>Buy low</span><i>→</i><span>Sell weird</span><i>→</i><span>Coast FI</span><i>→</i><strong>Aura maxed</strong>
          </div>
          <small className="market-disclaimer">Fictional beach economy. Real serialized cards. Zero financial advice.</small>
        </div>
        {profile ? (
          <aside className="market-wallet" aria-label="Current Exchange balance">
            <span>Signed in as</span><strong>{profile.username}</strong>
            <div><b>{profile.play_points}</b> Play Points</div>
            <div><b>{profile.win_points}</b> Win Points</div>
          </aside>
        ) : (
          <aside className="market-guest-note">
            <strong>THE SHUTTERS ARE OPEN</strong>
            <p>Browse the whole floor. Sign in when your inner beach capitalist is ready to stand on business—buy, sell, or rebalance the Hoard.</p>
            <GameButton to="/account" tone="gold">Enter the floor</GameButton>
          </aside>
        )}
      </header>

      <nav className="market-tabs" aria-label="Exchange sections">
        <button type="button" className={section === "browse" ? "is-active" : ""} onClick={() => setSection("browse")}>Browse <span>{listings.length}{hasMoreListings ? "+" : ""}</span></button>
        <button type="button" className={section === "sell" ? "is-active" : ""} onClick={() => setSection("sell")} disabled={!profile}>Sell</button>
        <button type="button" className={section === "mine" ? "is-active" : ""} onClick={() => setSection("mine")} disabled={!profile}>My Listings <span>{activeMyListings.length}</span></button>
      </nav>

      {message && <p className="market-message" role="status">{message}</p>}
      {!hasSupabaseConfiguration && <p className="market-message">The island signal is offline. The Exchange cannot load right now.</p>}

      {section === "browse" && (
        <section className="market-board" aria-labelledby="market-browse-title">
          <div className="market-board-tools">
            <div><p className="eyebrow">Live tide ticker</p><h2 id="market-browse-title">Invest in Bad Decisions</h2></div>
            <label>Search the Exchange<input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="NUMGNUM, edition, serial, or seller" /></label>
          </div>
          {isLoading ? <p className="market-empty">Reading the tide ledger…</p> : listings.length ? (
            <div ref={browseScrollRef} className="market-listing-grid market-scroll-region" tabIndex={0} aria-label="Exchange listings">
              {!visibleListings.length && <p className="market-empty market-empty--grid">No matching listings in this crate. Load the next one or broaden the thesis.</p>}
              {visibleListings.map((listing) => (
                <article className={listingClassName(listing.cards)} key={listing.id}>
                  <GroupedListingCards cards={listing.cards} />
                  <div className="market-listing-copy">
                    <span>{listing.cards.length} card{listing.cards.length === 1 ? "" : "s"} · offered by</span>
                    <h3>{listing.seller_username}<VerifiedCreatorMark username={listing.seller_username} /></h3>
                    <strong>{priceLabel(listing)}</strong>
                    {profile ? (
                      listing.is_mine
                        ? <button className="market-action market-action--quiet" type="button" onClick={() => { setSection("mine"); window.scrollTo({ top: 0, behavior: "smooth" }); }}>Your listing</button>
                        : <button className="market-action" type="button" disabled={!canAfford(listing) || isWorking} onClick={() => setPendingPurchase(listing)}>{canAfford(listing) ? "Buy cards" : "Not enough points"}</button>
                    ) : <GameButton to="/account" tone="sea">Sign in to buy</GameButton>}
                  </div>
                </article>
              ))}
              {(hasMoreListings || isLoadingMore) && (
                <div className="market-load-more" ref={loadMoreRef} aria-live="polite">
                  <button type="button" disabled={isLoadingMore} onClick={() => void loadPublicListings(true)}>
                    {isLoadingMore ? "Hauling in the next crate\u2026" : "Load more listings"}
                  </button>
                </div>
              )}
              {!hasMoreListings && <p className="market-end-of-ledger">End of the tide ledger · {listings.length} listing{listings.length === 1 ? "" : "s"} loaded</p>}
            </div>
          ) : <p className="market-empty">No alpha washed ashore. Broaden the thesis.</p>}
        </section>
      )}

      {section === "sell" && profile && (
        <section className="market-sell" aria-labelledby="market-sell-title">
          <div className="market-board-tools"><div><p className="eyebrow">Liquidate the duplicates</p><h2 id="market-sell-title">Fund Your Early Retirement</h2><p>List up to 20 serialized cards and stack the aura. Cards in your Battle Hoard, house display, or another listing stay safely locked.</p></div></div>
          <div className="market-price-rack">
            <label>Play Point price<input type="number" min="0" max="1000000" step="1" inputMode="numeric" value={playPrice} onChange={(event) => setPlayPrice(Math.max(0, Number(event.target.value) || 0))} /></label>
            <label>Win Point price<input type="number" min="0" max="1000000" step="1" inputMode="numeric" value={winPrice} onChange={(event) => setWinPrice(Math.max(0, Number(event.target.value) || 0))} /></label>
            <div><b>{selectedCardIDs.size}</b><span>selected</span></div>
            <GameButton tone="gold" disabled={isWorking || !selectedCardIDs.size || (playPrice === 0 && winPrice === 0)} onClick={() => void createListing()}>{isWorking ? "Working…" : "List this sale"}</GameButton>
          </div>
          {availableSellCards.length ? (
            <div className="market-sell-grid market-scroll-region" tabIndex={0} aria-label="Cards available to list">
              {availableSellCards.map((card) => (
                <article className={selectedCardIDs.has(card.id) ? "is-selected" : ""} key={card.id}>
                  <NumgnumCard level={card.level} edition={card.edition} shiny={card.is_shiny} founder={card.is_founder} serial={card.serial_number} founderName={card.founder_username} showsSerialBadge />
                  <button type="button" className="market-card-select" aria-pressed={selectedCardIDs.has(card.id)} onClick={() => toggleCard(card.id)}>{selectedCardIDs.has(card.id) ? "SELECTED" : "SELECT"}</button>
                </article>
              ))}
            </div>
          ) : <p className="market-empty">No cards are available to list. Check your Battle Hoard and current listings.</p>}
        </section>
      )}

      {section === "mine" && profile && (
        <section className="market-board" aria-labelledby="market-mine-title">
          <div className="market-board-tools"><div><p className="eyebrow">Your open positions</p><h2 id="market-mine-title">The Aura Portfolio</h2></div></div>
          {activeMyListings.length ? (
            <div className="market-listing-grid market-scroll-region" tabIndex={0} aria-label="Your Exchange listings">
              {activeMyListings.map((listing) => (
                <article className={listingClassName(listing.cards)} key={listing.id}>
                  <GroupedListingCards cards={listing.cards} />
                  <div className="market-listing-copy"><span>Your asking price</span><h3>{priceLabel(listing)}</h3><button className="market-action market-action--quiet" type="button" disabled={isWorking} onClick={() => void withdrawListing(listing)}>Withdraw listing</button></div>
                </article>
              ))}
            </div>
          ) : <p className="market-empty">Your Aura Portfolio has no open positions. List something weird.</p>}
        </section>
      )}

      {pendingPurchase && (
        <div className="market-dialog-backdrop" role="presentation">
          <section className="market-dialog" role="dialog" aria-modal="true" aria-labelledby="purchase-dialog-title">
            <img src="/art/brand/grinning-forge-emblem.webp" alt="" />
            <p className="eyebrow">Immediate and final</p>
            <h2 id="purchase-dialog-title">Complete Exchange?</h2>
            <p>Buy {pendingPurchase.cards.length} card{pendingPurchase.cards.length === 1 ? "" : "s"} from {pendingPurchase.seller_username} for {priceLabel(pendingPurchase)}?</p>
            <div className="button-row">
              <GameButton tone="gold" disabled={isWorking} onClick={() => void purchaseListing(pendingPurchase)}>{isWorking ? "Trading…" : "Complete purchase"}</GameButton>
              <button className="market-action market-action--quiet" type="button" disabled={isWorking} onClick={() => setPendingPurchase(null)}>Keep browsing</button>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
